import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';

import type { Process } from '../../../../bridge/types/process.ts';

type ScheduleProvenance = {
	/** The campaign the artifact belongs to, when the client holds that process. */
	campaignTitle: string | null;
	/** The chat the artifact came from, when the client has that chat loaded. */
	chatTitle: string | null;
	/** Set only when a chat title resolved, so a caller can offer to open it. */
	openChat: (() => void) | null;
};

// Where a scheduled item came from, resolved from state the client already holds.
//
// Two independent halves, because they come from different places and each can be absent
// on its own. The CAMPAIGN comes from the process map the Content space has already loaded
// and keyed by `process_id`, so it is available on a cold page load. The CHAT comes from
// `user_state.chats`, which only carries threads this client session has opened — a thread
// it has never seen has no title, and the map is pruned on disconnect. That is normal, not
// an error: the caller drops whichever clause is missing rather than fetching or spinning.
//
// The campaign half matters most for the question this answers. Two posts scheduled in one
// activation share a process, so naming the campaign tells the owner which piece of work a
// scheduled post belongs to — which a timestamp alone cannot.
export const useScheduleProvenance = (
	artifact: { chat_thread_id?: string | null; process_id?: string | null },
	processById?: Map<string, Process>,
): ScheduleProvenance => {
	const bridgeApi = useBridgeApi();
	const chatId = artifact.chat_thread_id ?? null;
	const processId = artifact.process_id ?? null;

	const campaignTitle =
		(processId ? processById?.get(processId)?.title : null) ?? null;

	// user_state is null while the socket is down, so never read chats directly.
	const chatTitle = chatId
		? (bridgeApi.user_state?.chats?.[chatId]?.title ?? null)
		: null;

	const openChat = useCallback(() => {
		if (!chatId) return;
		bridgeApi.sendAction({ type: 'do_switch_chat', chat_id: chatId });
		bridgeApi.sendAction({ type: 'do_navigate_to_space', space_type: 'chat' });
	}, [bridgeApi, chatId]);

	return { campaignTitle, chatTitle, openChat: chatTitle ? openChat : null };
};
